import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { Enums } from "@/integrations/supabase/types";

type Tier = Enums<"subscription_tier">;

const TIER_LEVELS: Record<Tier, number> = {
  free: 1,
  starter: 2,
  execution: 3,
  professional: 4,
  elite: 5,
};

interface EntitlementCheck {
  canAccess: boolean;
  requiredTier: Tier;
  userTier: Tier;
  isLocked: boolean;
  upgradeMessage: string;
}

interface UserEntitlements {
  tier: Tier;
  tierLevel: number;
  entitlements: string[]; // List of course slugs or bundle IDs the user owns
  loading: boolean;
  refresh: () => Promise<void>;
  checkAccess: (requiredTier: Tier, isFreePreview?: boolean) => EntitlementCheck;
  checkCourseAccess: (courseSlug: string, courseTier: Tier, isFreePreview: boolean) => Promise<boolean>;
}

export function useEntitlements(): UserEntitlements {
  const { user, hasActiveSubscription } = useAuth();
  const [tier, setTier] = useState<Tier>("free");
  const [entitlements, setEntitlements] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setTier("free");
      setEntitlements([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Get effective tier from subscription + entitlements
    const { data: tierData } = await supabase.rpc("user_effective_tier", {
      _user_id: user.id,
    });

    // Get all course/bundle entitlements
    const { data: entitlementData } = await supabase
      .from("user_entitlements")
      .select("entitlement_type, entitlement_value")
      .eq("user_id", user.id)
      .is("revoked_at", null)
      .or("expires_at.is.null,expires_at.gt.now()");

    const userTier: Tier = tierData || "free";
    setTier(userTier);

    // Extract course slugs and bundle IDs
    const userEntitlements = (entitlementData || [])
      .filter((e) => e.entitlement_type === "course" || e.entitlement_type === "bundle")
      .map((e) => e.entitlement_value);

    setEntitlements(userEntitlements);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const checkAccess = useCallback(
    (requiredTier: Tier, isFreePreview = false): EntitlementCheck => {
      const userLevel = TIER_LEVELS[tier];
      const requiredLevel = TIER_LEVELS[requiredTier];

      // Free content is always accessible
      if (requiredTier === "free" || isFreePreview) {
        return {
          canAccess: true,
          requiredTier,
          userTier: tier,
          isLocked: false,
          upgradeMessage: "",
        };
      }

      // Check tier level
      const hasTierAccess = userLevel >= requiredLevel;

      // Determine upgrade message based on required tier
      let upgradeMessage = "";
      if (!hasTierAccess) {
        switch (requiredTier) {
          case "starter":
            upgradeMessage = "Upgrade to Starter (R299) to unlock this content";
            break;
          case "execution":
            upgradeMessage = "Upgrade to Execution (R799) to unlock this content";
            break;
          case "professional":
            upgradeMessage = "Upgrade to Professional (R1999) to unlock this content";
            break;
          case "elite":
            upgradeMessage = "Join Mentorship Membership (R249/month) to unlock this content";
            break;
        }
      }

      return {
        canAccess: hasTierAccess,
        requiredTier,
        userTier: tier,
        isLocked: !hasTierAccess,
        upgradeMessage,
      };
    },
    [tier]
  );

  const checkCourseAccess = useCallback(
    async (courseSlug: string, courseTier: Tier, isFreePreview: boolean): Promise<boolean> => {
      if (!user) return false;

      // Use the database function for accurate access check
      const { data } = await supabase.rpc("can_access_course", {
        _user_id: user.id,
        _course_slug: courseSlug,
      });

      return !!data;
    },
    [user]
  );

  return {
    tier,
    tierLevel: TIER_LEVELS[tier],
    entitlements,
    loading,
    refresh,
    checkAccess,
    checkCourseAccess,
  };
}

export { TIER_LEVELS };
export type { Tier, EntitlementCheck };
